import React, { useEffect, useState, type JSX } from 'react';
import { Helmet } from 'react-helmet';
import { Box, Container, Title, Text, Accordion, Button, Grid, Card, ThemeIcon, Group } from '@mantine/core';
import { Link } from 'react-router-dom';
import { IconArrowRight, IconQuestionMark, IconMessage, IconLock, IconBrain, IconClockHour4 } from '@tabler/icons-react';

/**
 * FAQ Page - Comprehensive questions and answers about the service
 * This page is optimized for SEO with FAQ schema markup
 */
export function FAQPage(): JSX.Element {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // State to track open accordion items for analytics
  const [openItems, setOpenItems] = useState<string[]>([]);

  // Tracking function for FAQ engagement
  const trackFaqEngagement = (questionId: string, isOpening: boolean) => {
    // In a real implementation, you'd send this to your analytics
    console.log(`User ${isOpening ? 'opened' : 'closed'} FAQ: ${questionId}`);
    
    if (isOpening) {
      setOpenItems(prev => [...prev, questionId]);
    } else {
      setOpenItems(prev => prev.filter(id => id !== questionId));
    }
  };

  // FAQ categories and questions
  const faqCategories = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: <IconMessage size={24} className="text-[#C2A4D6]" />,
      questions: [
        {
          id: 'what-is-feelheard',
          question: 'What is FeelHeard?',
          answer: 'FeelHeard is an AI-powered emotional support platform that provides a judgment-free space for processing feelings, gaining insights, and improving emotional wellbeing. Our AI Reflection Guide, Cora, engages in meaningful conversations to help you explore your thoughts and emotions.'
        },
        {
          id: 'how-to-start',
          question: 'How do I get started with FeelHeard?',
          answer: 'Getting started is simple: 1) Sign up for a free 7-day trial, 2) Create your account, 3) Begin your first conversation with Cora. You can start typing or speaking immediately, sharing whatever is on your mind. There\'s no preparation needed - just speak naturally as you would to a supportive friend.'
        },
        {
          id: 'what-to-talk-about',
          question: 'What should I talk to Cora about?',
          answer: 'You can talk to Cora about virtually anything on your mind: daily stressors, challenging emotions, relationship issues, work concerns, personal goals, or even moments of joy and accomplishment. Cora is designed to provide reflective support for a wide range of emotional experiences.'
        },
        {
          id: 'conversation-length',
          question: 'How long should my conversations be?',
          answer: 'Conversations can be as short or as long as you need. Some users benefit from quick 5-minute check-ins, while others prefer deeper 20-30 minute explorations. There are no time limits - use the service in whatever way feels most supportive for your current needs.'
        }
      ]
    },
    {
      id: 'privacy-security',
      title: 'Privacy & Security',
      icon: <IconLock size={24} className="text-[#F4B6A1]" />,
      questions: [
        {
          id: 'data-privacy',
          question: 'How is my data protected?',
          answer: 'Your privacy is our top priority. All conversations are encrypted end-to-end, and we maintain strict data protection policies that exceed industry standards. Your personal information and conversation content are never shared with third parties without your explicit consent.'
        },
        {
          id: 'conversation-storage',
          question: 'Are my conversations stored, and for how long?',
          answer: 'Conversations are stored securely to provide you with conversation history and continuity. With the basic plan, conversations are retained for 7 days; with premium plans, they\'re stored for longer periods. You can delete your conversation history at any time from your account settings.'
        },
        {
          id: 'data-use',
          question: 'How is my conversation data used?',
          answer: 'Your conversation data is used only to provide and improve the service for you. This includes allowing you to access your conversation history, improving Cora\'s responses to you specifically, and maintaining service continuity. We do not sell your data or use it for advertising purposes.'
        },
        {
          id: 'delete-data',
          question: 'Can I delete my data?',
          answer: 'Yes, you have complete control over your data. You can delete individual conversations or your entire conversation history at any time from your account settings. You can also request a complete deletion of your account and all associated data by contacting our support team.'
        }
      ]
    },
    {
      id: 'subscription-billing',
      title: 'Subscription & Billing',
      icon: <IconClockHour4 size={24} className="text-[#B3D9E6]" />,
      questions: [
        {
          id: 'subscription-options',
          question: 'What subscription options are available?',
          answer: 'We offer two main subscription tiers: Text Companion ($9.99/month) for text-based emotional support, and Voice Connect ($19.99/month) which adds voice conversation capabilities and advanced features. Both plans are available monthly or annually (with a 20% discount for annual billing).'
        },
        {
          id: 'cancel-subscription',
          question: 'How do I cancel my subscription?',
          answer: 'You can cancel your subscription at any time through your account settings page. After cancellation, you\'ll continue to have access to your subscription features until the end of your current billing period. We don\'t charge cancellation fees, and there are no long-term commitments.'
        },
        {
          id: 'refund-policy',
          question: 'What is your refund policy?',
          answer: 'We offer a satisfaction guarantee for new subscribers. If you don\'t feel genuinely supported after your first 5 conversations, we\'ll refund your subscription. For other refund requests, please contact our support team, and we\'ll address your concerns on a case-by-case basis.'
        },
        {
          id: 'payment-methods',
          question: 'What payment methods do you accept?',
          answer: 'We accept all major credit and debit cards (Visa, Mastercard, American Express, Discover) as well as PayPal. All payment processing is handled securely through Stripe, and we never store your full payment details on our servers.'
        }
      ]
    },
    {
      id: 'using-service',
      title: 'Using the Service',
      icon: <IconBrain size={24} className="text-[#A9D0A1]" />,
      questions: [
        {
          id: 'vs-therapy',
          question: 'Is FeelHeard the same as therapy?',
          answer: 'No, FeelHeard is not therapy and does not replace professional mental health services. It\'s a supplemental emotional support tool designed to help with reflection and emotional processing. While many users find it beneficial alongside therapy, it\'s not a substitute for professional treatment of clinical conditions.'
        },
        {
          id: 'voice-vs-text',
          question: 'What are the differences between voice and text conversations?',
          answer: 'Text conversations are available on all plans and allow for thoughtful, written expression. Voice conversations (available on the Voice Connect plan) enable you to speak naturally and hear responses, which many users find more intuitive and emotionally resonant. Both options provide the same quality of emotional support.'
        },
        {
          id: 'conversation-frequency',
          question: 'How often should I use FeelHeard?',
          answer: 'This varies by individual needs. Some users benefit from daily check-ins for consistent emotional processing, while others prefer using the service during specific challenging times or for weekly reflections. We recommend experimenting to find a rhythm that supports your emotional wellbeing goals.'
        },
        {
          id: 'crisis-support',
          question: 'Can I use FeelHeard in a crisis?',
          answer: 'FeelHeard is not designed for crisis intervention. If you\'re experiencing a mental health emergency or having thoughts of harming yourself or others, please contact a crisis helpline (like 988 Suicide & Crisis Lifeline in the US), call your local emergency number, or go to your nearest emergency room.'
        }
      ]
    }
  ];

  // Create structured data for FAQ schema
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqCategories.flatMap(category => 
      category.questions.map(q => ({
        "@type": "Question",
        "name": q.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": q.answer
        }
      }))
    )
  };

  return (
    <>
      {/* SEO Metadata */}
      <Helmet>
        <title>Frequently Asked Questions | FeelHeard AI Support</title>
        <meta name="description" content="Find answers to common questions about FeelHeard's AI emotional support service, including privacy, billing, usage, and getting started." />
        <meta name="keywords" content="AI emotional support FAQ, emotional wellbeing questions, digital wellness help, FeelHeard questions, AI mental health FAQ" />
        <link rel="canonical" href="https://feelheard.me/faq" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://feelheard.me/faq" />
        <meta property="og:title" content="Frequently Asked Questions | FeelHeard AI Support" />
        <meta property="og:description" content="Find answers to common questions about FeelHeard's AI emotional support service, including privacy, billing, usage, and getting started." />
        <meta property="og:image" content="https://feelheard.me/assets/og-image.jpg" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://feelheard.me/faq" />
        <meta property="twitter:title" content="Frequently Asked Questions | FeelHeard AI Support" />
        <meta property="twitter:description" content="Find answers to common questions about FeelHeard's AI emotional support service, including privacy, billing, usage, and getting started." />
        <meta property="twitter:image" content="https://feelheard.me/assets/og-image.jpg" />
        
        {/* Structured Data for FAQ Page */}
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <Box py={60} className="bg-gray-50">
        <Container size="lg">
          <Box className="text-center mb-10">
            <div className="inline-block bg-[#C2A4D6]/20 px-4 py-2 rounded-full mb-4">
              <span className="text-[#C2A4D6] font-medium">Questions & Answers</span>
            </div>
            <Title order={1} className="text-4xl md:text-5xl font-bold text-gray-800 mb-4 font-['Poppins']">
              Frequently Asked <span className="text-[#C2A4D6]">Questions</span>
            </Title>
            <Text className="text-lg text-gray-600 max-w-3xl mx-auto">
              Find answers to common questions about FeelHeard's AI emotional support service. 
              If you can't find what you're looking for, feel free to contact our support team.
            </Text>
          </Box>
          
          {/* Category Cards */}
          <Grid className="mb-16">
            {faqCategories.map((category) => (
              <Grid.Col key={category.id} span={{ base: 6, sm: 6, md: 3 }}>
                <Card 
                  shadow="sm" 
                  p="lg" 
                  radius="md" 
                  withBorder 
                  className="h-full transition-all hover:shadow-md text-center"
                  component="a"
                  href={`#${category.id}`}
                >
                  <ThemeIcon size={50} radius="md" className="mb-4 mx-auto bg-[#C2A4D6]/10">
                    {category.icon}
                  </ThemeIcon>
                  <Title order={3} className="text-xl font-bold mb-3">{category.title}</Title>
                  <Text className="text-gray-600">{category.questions.length} questions</Text>
                </Card>
              </Grid.Col>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FAQ Sections */}
      <Box py={60}>
        <Container size="lg">
          {faqCategories.map((category) => (
            <Box key={category.id} id={category.id} className="mb-16 scroll-mt-24">
              <Group justify="space-between" className="mb-6">
                <Group>
                  <ThemeIcon size={40} radius="md" className="bg-[#C2A4D6]/10">
                    {category.icon}
                  </ThemeIcon>
                  <Title order={2} className="text-2xl md:text-3xl font-bold text-gray-800 font-['Poppins']">
                    {category.title}
                  </Title>
                </Group>
              </Group>
              
              <Accordion 
                variant="separated" 
                radius="md"
                onChange={(value) => {
                  // Track which question was clicked
                  const allQuestionIds = category.questions.map(q => q.id);
                  const openQuestionIds = value && value.length > 0 ? [value] : [];
                  
                  // Find newly opened or closed questions
                  const newlyOpened = openQuestionIds.filter(id => !openItems.includes(id));
                  const newlyClosed = openItems.filter(id => id && !openQuestionIds.includes(id) && allQuestionIds.includes(id));
                  
                  // Track events
                  newlyOpened.forEach(id => id && trackFaqEngagement(id, true));
                  newlyClosed.forEach(id => id && trackFaqEngagement(id, false));
                }}
              >
                {category.questions.map((q) => (
                  <Accordion.Item key={q.id} value={q.id}>
                    <Accordion.Control>
                      <Text className="text-lg font-medium">{q.question}</Text>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Text className="text-gray-600">{q.answer}</Text>
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Box>
          ))}
          
          {/* Still Have Questions */}
          <Box className="bg-gray-50 p-8 rounded-xl text-center">
            <Title order={3} className="text-2xl font-bold mb-4">Still Have Questions?</Title>
            <Text className="text-gray-600 mb-6 max-w-2xl mx-auto">
              If you couldn't find the answer you were looking for, our support team is here to help. 
              We're committed to ensuring you have the best experience with FeelHeard.
            </Text>
            <Button 
              component="a"
              href="mailto:support@somatopia.com"
              size="lg"
              radius="xl"
              className="bg-[#C2A4D6] hover:bg-[#B390C9] text-white px-8 transition-all"
              leftSection={<IconQuestionMark size={18} />}
            >
              Contact Support
            </Button>
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box py={60} className="bg-[#F4B6A1]/10">
        <Container size="md" className="text-center">
          <Title order={2} className="text-3xl font-bold text-gray-800 mb-4 font-['Poppins']">
            Ready to <span className="text-[#F4B6A1]">Experience</span> FeelHeard?
          </Title>
          <Text className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
            Get started with your free 7-day trial today and discover the benefits of AI-powered emotional support.
          </Text>
          
          <Button 
            component={Link} 
            to="/register"
            size="lg"
            radius="xl"
            className="bg-[#C2A4D6] hover:bg-[#B390C9] text-white px-8 py-4 transition-all transform hover:scale-105"
            rightSection={<IconArrowRight size={18} />}
          >
            Try Free For 7 Days
          </Button>
          
          <Text className="text-gray-500 mt-4">
            No credit card required. Cancel anytime.
          </Text>
        </Container>
      </Box>
    </>
  );
}

export default FAQPage;